import React, {useContext, useEffect, useState, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Context} from '../index';
import MyButton from '../UI/MyButton/MyButton';
import {MaterialReactTable} from "material-react-table";
import ReceiptService from '../services/ReceiptService';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    Paper,
    Fab
} from '@mui/material';
import {Add, ExitToApp} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Receipts() {
    const {store} = useContext(Context);
    const {t} = useTranslation();
    const [receipts, setReceipts] = useState([]);
    const navigate = useNavigate();

    const pagination = {
        pageSize: 100
    };

    const columns = useMemo(
        () => [
            {
                accessorFn: (row) => {
                    const counterparties = row.counterparties || [];
                    if (counterparties.length === 0) {
                        return t('receipts.noCounterparties');
                    }
                    if (counterparties.length === 1) {
                        return counterparties[0].name;
                    }
                    return t('receipts.counterpartiesCount', {count: counterparties.length});
                },
                id: 'counterparties',
                header: t('label.counterparty'),
                size: 200,
                Cell: ({ row }) => {
                    const counterparties = row.original.counterparties || [];
                    if (counterparties.length === 0) {
                        return <span style={{ color: '#999' }}>{t('receipts.noCounterparties')}</span>;
                    }
                    if (counterparties.length === 1) {
                        return (
                            <div>
                                <div>{counterparties[0].name}</div>
                                <div style={{ fontSize: '10px', color: '#666' }}>
                                    {t('receipts.itemsCount', {count: counterparties[0].itemsCount})}
                                </div>
                            </div>
                        );
                    }

                    // For multiple counterparties show list with details
                    return (
                        <div>
                            <div style={{ fontWeight: 'bold' }}>
                                {t('receipts.counterpartiesCountColon', {count: counterparties.length})}
                            </div>
                            {counterparties.map((cp, index) => (
                                <div key={cp.id} style={{
                                    fontSize: '10px',
                                    color: '#666',
                                    marginTop: '2px'
                                }}>
                                    • {t('receipts.counterpartyItemsCount', {name: cp.name, count: cp.itemsCount})}
                                </div>
                            ))}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'store.name',
                header: t('label.warehouse'),
                size: 200
            },
            {
                accessorFn: (row) => {
                    const date = new Date(row.receiptDate);
                    return date.toLocaleDateString('ru-RU');
                },
                id: 'receiptDate',
                header: t('receipts.receiptDate'),
                size: 150
            },
            {
                accessorFn: (row) => row.items?.length || 0,
                id: 'itemsCount',
                header: t('receipts.items'),
                size: 100
            },
            {
                accessorKey: 'status',
                header: t('label.status'),
                size: 120,
                Cell: ({ row }) => {
                    const status = row.original.status;
                    const hasSendError = row.original.hasSendError;
                    const errorMessage = row.original.errorMessage;

                    const statusMap = {
                        draft: t('status.draft'),
                        sent: t('status.sent'),
                        error: t('status.error')
                    };

                    // Special status for drafts with send errors
                    let displayStatus = statusMap[status] || status;
                    let color = status === 'sent' ? '#4caf50' : '#ff9800';

                    if (status === 'draft' && hasSendError) {
                        displayStatus = t('receipts.draftWithError');
                        color = '#f44336';
                    }

                    return (
                        <div>
                            <span style={{
                                color: color,
                                fontWeight: 'bold'
                            }}>
                                {displayStatus}
                            </span>
                            {hasSendError && errorMessage && (
                                <div style={{
                                    fontSize: '10px',
                                    color: '#666',
                                    marginTop: '2px',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }} title={errorMessage}>
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorFn: (row) => {
                    const date = new Date(row.updatedAt);
                    return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
                },
                id: 'updatedAt',
                header: t('receipts.updated'),
                size: 150
            }
        ],
        [t],
    );

    useEffect(() => {
        getUserReceipts();
    }, []);

    async function getUserReceipts() {
        try {
            const response = await ReceiptService.getUserReceipts();
            setReceipts(response.data);
        } catch (error) {
            alert(t('receipts.errorLoadingReceipts'));
        }
    }

    function handleCreateReceipt() {
        navigate('/receipts/new');
    }

    function handleEditReceipt(receiptId) {
        navigate(`/receipts/${receiptId}`);
    }

    async function handleDeleteReceipt(receiptId) {
        if (!window.confirm(t('receipts.confirmDeleteReceipt'))) {
            return;
        }

        try {
            await ReceiptService.deleteReceipt(receiptId);
            alert(t('receipts.receiptDeleted'));
            await getUserReceipts();
        } catch (error) {
            alert(t('receipts.errorDeletingReceipt'));
        }
    }

    function handleLogout() {
        navigate('/login');
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <AppBar position="static" sx={{
                background: 'linear-gradient(135deg, #f24405 0%, #a62f04 100%)',
                marginBottom: 3
            }}>
                <Toolbar>
                    <div style={{ flexGrow: 1, fontSize: '1.25rem', fontWeight: 500 }}>
                        {t('receipts.headerTitle')}
                    </div>
                    <div style={{ marginRight: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {store.user.email}
                        <LanguageSwitcher />
                    </div>
                    <Button
                        color="inherit"
                        startIcon={<ExitToApp />}
                        onClick={async () => {
                            await store.logout();
                            handleLogout();
                        }}
                    >
                        {t('btn.logout')}
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Main content */}
            <Container maxWidth="xl">
                {/* Title and create button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <h2 style={{ margin: 0, fontSize: '2.125rem', fontWeight: 400 }}>
                        {t('receipts.title')}
                    </h2>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreateReceipt}
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' },
                            fontSize: '16px',
                            padding: '10px 20px'
                        }}
                    >
                        {t('btn.newReceipt')}
                    </Button>
                </Box>

                {/* Receipts table */}
                <Paper elevation={2} sx={{ padding: 2 }}>
                    {receipts.length > 0 ? (
                        <MaterialReactTable
                            columns={columns}
                            data={receipts}
                            enablePagination={false}
                            enableTopToolbar={false}
                            enableBottomToolbar={false}
                            enableSorting={false}
                            enableColumnFilters={false}
                            enableColumnActions={false}
                            enableDensityToggle={false}
                            enableFullScreenToggle={false}
                            enableHiding={false}
                            enableRowActions
                            muiTableContainerProps={{
                                sx: {
                                    maxHeight: '600px'
                                }
                            }}
                            renderRowActions={({ row }) => (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditReceipt(row.original._id);
                                        }}
                                        sx={{
                                            backgroundColor: row.original.status === 'draft' ? '#f24405' : '#ff9800',
                                            '&:hover': {
                                                backgroundColor: row.original.status === 'draft' ? '#a62f04' : '#f57c00'
                                            },
                                            fontSize: '12px',
                                            minWidth: 'auto'
                                        }}
                                    >
                                        {row.original.status === 'draft' ? t('btn.edit') : t('btn.view')}
                                    </Button>
                                    {(row.original.status === 'draft' || row.original.status === 'error') && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteReceipt(row.original._id);
                                            }}
                                            sx={{
                                                fontSize: '12px',
                                                minWidth: 'auto'
                                            }}
                                        >
                                            {t('btn.delete')}
                                        </Button>
                                    )}
                                </Box>
                            )}
                            muiTableBodyRowProps={({row}) => ({
                                sx: {
                                    backgroundColor: row.original.hasSendError ? '#ffebee' : '#ffffff',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: row.original.hasSendError ? '#ffcdd2' : '#f5f5f5',
                                    }
                                },
                                onClick: () => handleEditReceipt(row.original._id)
                            })}
                        />
                    ) : (
                        <Box sx={{
                            textAlign: 'center',
                            padding: 6,
                            color: '#666',
                            backgroundColor: '#fafafa',
                            borderRadius: 2
                        }}>
                            <h3 style={{ margin: 0, marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 500 }}>
                                {t('msg.noReceipts')}
                            </h3>
                            <div style={{ fontSize: '1rem' }}>
                                {t('msg.noReceiptsHint')}
                            </div>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Floating Action Button для мобильных устройств */}
            <Fab
                aria-label="add"
                onClick={handleCreateReceipt}
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    backgroundColor: '#f24405',
                    '&:hover': { backgroundColor: '#a62f04' },
                    display: { xs: 'flex', sm: 'none' } // Показываем только на мобильных
                }}
            >
                <Add />
            </Fab>
        </Box>
    );
}